import { z } from 'zod';
import { endpoint } from './endpoint.js';
import { OfferIdSchema } from '../schemas/offer.js';
import {
  AcceptVersionBodySchema,
  AcceptVersionResponseSchema,
} from '../schemas/offer-version-diff.js';
import {
  CreatePublicLinkResponseSchema,
  PublicLinkStatusSchema,
  PublicOfferViewResponseSchema,
} from '../schemas/offer-public-link.js';
import {
  OfferPeriodInquiryBodySchema,
  OfferPeriodInquiryResponseSchema,
} from '../schemas/offer-period-inquiry.js';
import { ClientMediaUnitDetailResponseSchema } from '../schemas/media-units.js';

export const publicOfferLink = {
  /** POST /offers/:id/public-link — generates a new public share link (sales user only). */
  createLink: endpoint(
    'POST',
    '/offers/:id/public-link',
    z.object({ id: OfferIdSchema }).strict(),
    CreatePublicLinkResponseSchema,
  ),

  /** GET /offers/:id/public-link — returns link state without the token (sales user only). */
  linkStatus: endpoint(
    'GET',
    '/offers/:id/public-link',
    z.object({ id: OfferIdSchema }).strict(),
    PublicLinkStatusSchema,
  ),

  /**
   * DELETE /offers/:id/public-link — revokes the active public link (sales user only).
   * Response is z.void() — the controller returns 204 No Content, and callEndpoint's
   * 204 branch parses `undefined`, not `{}` (see packages/contracts/AGENTS.md).
   */
  revokeLink: endpoint(
    'DELETE',
    '/offers/:id/public-link',
    z.object({ id: OfferIdSchema }).strict(),
    z.void(),
  ),

  /**
   * GET /public/offers/:token/client-view — anonymous public client view.
   * defaultCredentials: 'omit' — never sends auth cookies for anonymous requests.
   */
  publicClientView: endpoint(
    'GET',
    '/public/offers/:token/client-view',
    z.object({ token: z.string().min(1) }).strict(),
    PublicOfferViewResponseSchema,
    'omit',
  ),

  /**
   * POST /public/offers/:token/accept — anonymous public acceptance.
   * defaultCredentials: 'omit' — never sends auth cookies for anonymous requests.
   */
  publicAccept: endpoint(
    'POST',
    '/public/offers/:token/accept',
    z.object({ token: z.string().min(1) }).merge(AcceptVersionBodySchema),
    AcceptVersionResponseSchema,
    'omit',
  ),

  // publicReject (POST /public/offers/:token/reject) removed — withdrawing an
  // acceptance is exclusively a staff action (public-offer-link / Public Offer
  // Rejection Endpoint, REMOVED). Rejection stays available to staff through
  // the authenticated offerVersions.reject endpoint.

  /**
   * POST /public/offers/:token/period-inquiry — anonymous public period inquiry
   * submission. defaultCredentials: 'omit' — never sends auth cookies for
   * anonymous requests.
   */
  publicSubmitPeriodInquiry: endpoint(
    'POST',
    '/public/offers/:token/period-inquiry',
    z.object({ token: z.string().min(1) }).merge(OfferPeriodInquiryBodySchema),
    OfferPeriodInquiryResponseSchema,
    'omit',
  ),

  /**
   * GET /public/offers/:token/units/:unitId — anonymous token-scoped
   * media-unit detail. defaultCredentials: 'omit' — never sends auth cookies
   * for anonymous requests.
   */
  publicUnitDetail: endpoint(
    'GET',
    '/public/offers/:token/units/:unitId',
    z.object({ token: z.string().min(1), unitId: z.coerce.number().int().positive() }).strict(),
    ClientMediaUnitDetailResponseSchema,
    'omit',
  ),
};
